import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';

// Wait briefly before showing anything so fast loads don't flash a spinner.
const appear = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

const STILL_WORKING_AFTER_MS = 8000;
const OFFER_RELOAD_AFTER_MS = 20000;

// True once the same step has been showing for `afterMs`, so a slow load says it
// is still going instead of looking frozen. Restarts whenever the step changes.
const useTakingAWhile = (step: string | undefined, afterMs: number): boolean => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    const timer = window.setTimeout(() => setSlow(true), afterMs);
    return () => window.clearTimeout(timer);
  }, [step, afterMs]);
  return slow;
};

type LoadingSpinnerProps = {
  /** Short status, e.g. "Loading services". Omit for a plain spinner. */
  label?: string;
  /** One quiet line under the label, e.g. "Keep this page open." */
  detail?: string;
  /** Fill the whole viewport (app start, route guards). Otherwise fills the page area. */
  fullScreen?: boolean;
  /** Fit inside a card or panel instead of taking up the page. */
  inline?: boolean;
  /** Offer a page reload when stuck. On by default for page loaders; turn off while saving. */
  allowReload?: boolean;
};

/** The one loading state used across Scheduify. */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ label, detail, fullScreen = false, inline = false, allowReload = !inline }) => {
  const slow = useTakingAWhile(label, STILL_WORKING_AFTER_MS);
  // A stalled connection can leave a load hanging; a reload opens a fresh one.
  const stuck = useTakingAWhile(label, OFFER_RELOAD_AFTER_MS) && allowReload;
  const note = stuck ? 'This is taking longer than usual.' : slow ? 'Still working, thanks for waiting…' : detail;
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      sx={{
        width: '100%',
        minHeight: inline ? 160 : fullScreen ? '100dvh' : '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: fullScreen ? 'background.default' : 'transparent',
        opacity: 0,
        animation: `${appear} 240ms ease 180ms forwards`,
        '@media (prefers-reduced-motion: reduce)': { animationDuration: '1ms' },
      }}
    >
      <Box sx={{ position: 'relative', width: 48, height: 48 }}>
        <CircularProgress variant="determinate" value={100} size={48} thickness={4}
          sx={{ position: 'absolute', inset: 0, color: 'rgba(25,118,210,.14)' }} />
        <CircularProgress size={48} thickness={4} disableShrink
          sx={{ position: 'absolute', inset: 0, color: 'primary.main', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
      </Box>
      {label || note ? <Box sx={{ minHeight: 44, textAlign: 'center', px: 2 }}>
        {label ? (
          <Typography key={label} variant="body2" color="text.secondary" fontWeight={750}
            sx={{ animation: `${appear} 240ms ease forwards` }}>{label}</Typography>
        ) : null}
        {note ? (
          <Typography key={note} variant="caption" color="text.secondary" display="block"
            sx={{ mt: 0.5, animation: `${appear} 240ms ease forwards` }}>{note}</Typography>
        ) : null}
      </Box> : null}
      {stuck ? (
        <Button variant="outlined" size="small" onClick={() => window.location.reload()} sx={{ borderRadius: 999, fontWeight: 800 }}>
          Reload page
        </Button>
      ) : null}
    </Box>
  );
};

export default LoadingSpinner;
